/**
 * Service Worker Registration — FIDSNet Display Client
 * ─────────────────────────────────────────────────────────────────────────────
 * Registers the service worker for offline caching and PWA functionality.
 */

export function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) {
    console.log("[SW] Service workers not supported");
    return;
  }

  // Don't register service worker in development — causes stale cache issues with Vite HMR
  if (import.meta.env.DEV) {
    // Unregister any existing SW from previous dev sessions
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      registrations.forEach((reg) => {
        reg.unregister();
        console.log("[SW] Unregistered stale dev service worker");
      });
    });
    return;
  }

  window.addEventListener("load", async () => {
    try {
      const registration = await navigator.serviceWorker.register("/service-worker.js", {
        scope: "/",
      });

      console.log("[SW] Service worker registered:", registration.scope);

      // Check for updates every 5 minutes
      setInterval(
        () => {
          registration.update();
        },
        5 * 60 * 1000,
      );

      // Handle updates
      registration.addEventListener("updatefound", () => {
        const newWorker = registration.installing;
        if (!newWorker) return;

        newWorker.addEventListener("statechange", () => {
          if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
            console.log("[SW] New version available — will activate on next load");
            // Auto-activate new version (display screens should always get latest)
            newWorker.postMessage({ type: "SKIP_WAITING" });
          }
        });
      });

      // Reload when new SW takes control
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        console.log("[SW] New service worker activated — reloading");
        window.location.reload();
      });
    } catch (error) {
      console.error("[SW] Registration failed:", error);
    }
  });
}

/**
 * Sends a layout URL to the service worker for pre-caching.
 * Call this whenever the display URL changes.
 */
export function cacheLayoutUrl(url) {
  if (navigator.serviceWorker?.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: "CACHE_LAYOUT_URL",
      url,
    });
  }
}
